import { useEffect, useState } from 'react';
import { X, Lock, Clock3, AlertTriangle } from 'lucide-react';
import useBoardStore from '../../store/boardStore';
import useMetricsStore from '../../store/metricsStore';
import PeriodPicker, { defaultPeriod } from '../metrics/PeriodPicker';
import PhaseBarChart from '../metrics/PhaseBarChart';
import WeeklyBarChart from '../metrics/WeeklyBarChart';
import CumulativeFlowChart from '../metrics/CumulativeFlowChart';
import PercentileStat from '../metrics/PercentileStat';
import { formatAbsolute } from '../../utils/formatRelative';
import { formatDuration, phaseLabel, phaseColor } from '../../utils/metricsFormat';

function SectionTitle({ children }) {
  return <div className="text-[11px] uppercase tracking-wider text-text-secondary font-semibold mb-2">{children}</div>;
}

// "Quantos cartões entram na conta e quantos ficam de fora" (pedido do
// marco) — lido direto de coverage (BOARD_METRICS_SQL, M6a), sem
// recalcular nada no cliente.
function CoverageBanner({ coverage }) {
  const total = coverage.cards_total ?? 0;
  if (total === 0) {
    return (
      <div className="text-xs text-text-secondary">
        Nenhum cartão neste quadro (no recorte de grupos que você acessa).
      </div>
    );
  }
  const outOfStart = coverage.start_unknown ?? 0;
  const outOfPhase = coverage.cards_with_unphased ?? 0;
  return (
    <div className="flex items-start gap-2 bg-white/5 border border-border/50 rounded-lg px-3 py-2.5">
      <Clock3 className="w-4 h-4 text-text-secondary shrink-0 mt-0.5" />
      <div className="text-xs text-text-secondary">
        <span className="text-text-primary font-medium">{total} cartões</span> no recorte deste quadro.{' '}
        {outOfStart > 0
          ? `${outOfStart} sem início conhecido (fora de cycle/lead time). `
          : 'Todos com início conhecido. '}
        {outOfPhase > 0 && `${outOfPhase} com alguma etiqueta sem fase (tempo parcial não contabilizado).`}
      </div>
    </div>
  );
}

function AgingList({ items }) {
  if (!items.length) return <div className="text-xs text-text-secondary py-3">Nada parado em andamento ou aguardando.</div>;
  return (
    <div className="space-y-1.5">
      {items.map((it) => (
        <div key={it.card_id} className="flex items-center gap-3 bg-main border border-border/50 rounded-lg px-3 py-2 text-xs">
          <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold text-white ${phaseColor(it.phase).bg}`}>
            {phaseLabel(it.phase)}
          </span>
          <span className="flex-1 min-w-0 truncate text-text-primary" title={it.card_title}>{it.card_title}</span>
          <span className="text-text-secondary shrink-0">{it.assignee_name || 'sem responsável'}</span>
          <span className="text-text-primary font-medium shrink-0 tabular-nums" title={formatAbsolute(it.since)}>
            {formatDuration(it.age_ms)}
          </span>
        </div>
      ))}
    </div>
  );
}

// Painel "Métricas do Quadro" (M6b) — aberto pelo menu do quadro
// (BoardToolbar.jsx), ao lado de "Registros do Quadro". Mesma permissão do
// histórico (activity.read no backend); mesmo gate visual no front
// (canSeeBoardTools, calculado por quem chama).
export default function BoardMetricsModal({ onClose }) {
  const currentBoard = useBoardStore((s) => s.currentBoard);
  const boardMetrics = useMetricsStore((s) => s.boardMetrics);
  const loadingBoardMetrics = useMetricsStore((s) => s.loadingBoardMetrics);
  const boardMetricsError = useMetricsStore((s) => s.boardMetricsError);
  const fetchBoardMetrics = useMetricsStore((s) => s.fetchBoardMetrics);
  const [period, setPeriod] = useState(() => defaultPeriod(30));

  const boardId = currentBoard?.id;
  const data = boardId ? boardMetrics[boardId] : null;

  useEffect(() => {
    if (!boardId) return;
    fetchBoardMetrics(boardId, { from: period.from.toISOString(), to: period.to.toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, period]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-xl border border-border shadow-2xl w-[880px] max-w-[95vw] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-text-secondary">Métricas do quadro</div>
            <h3 className="text-lg font-semibold text-text-primary">{currentBoard?.name}</h3>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-border/50 flex items-center justify-between flex-wrap gap-2">
          <PeriodPicker value={period} onChange={setPeriod} />
          {data && (
            <div className="text-[11px] text-text-secondary">
              {formatAbsolute(data.window.from)} – {formatAbsolute(data.window.to)} · fuso {data.window.timezone}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loadingBoardMetrics && !data && (
            <div className="text-center py-12 text-sm text-text-secondary">Carregando...</div>
          )}

          {boardMetricsError?.status === 403 && (
            <div className="p-8 flex flex-col items-center justify-center text-center">
              <Lock className="w-12 h-12 text-danger/40 mb-3" />
              <div className="text-sm font-semibold text-text-primary mb-1">Sem permissão para ver as métricas deste quadro</div>
              <div className="text-xs text-text-secondary">Peça acesso a um administrador do quadro.</div>
            </div>
          )}

          {boardMetricsError && boardMetricsError.status !== 403 && (
            <div className="text-center py-12 text-sm text-danger">{boardMetricsError.message}</div>
          )}

          {data && (
            data.workflow.column_id == null ? (
              <div className="flex items-start gap-2 bg-warning/10 border border-warning/30 rounded-lg px-3 py-2.5">
                <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
                <div className="text-xs text-text-primary">
                  <div className="font-medium mb-0.5">Sem coluna de fluxo configurada</div>
                  <div className="text-text-secondary">
                    Este quadro não tem uma coluna de status marcada como fluxo de trabalho — configure a coluna de
                    status para as métricas aparecerem aqui.
                  </div>
                </div>
              </div>
            ) : (
              <>
                <CoverageBanner coverage={data.coverage} />

                <div>
                  <SectionTitle>WIP por fase (agora)</SectionTitle>
                  <PhaseBarChart
                    items={data.wip.map((w) => ({ phase: w.phase, value: w.cards }))}
                    formatValue={(v) => `${v}`}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <PercentileStat label="Cycle time" stat={data.cycle_time} hint="Do início ao fim de cada ciclo (started → completed)." />
                  <PercentileStat label="Lead time" stat={data.lead_time} hint="Da criação até a 1ª conclusão." />
                </div>

                <div>
                  <SectionTitle>Throughput por semana</SectionTitle>
                  <WeeklyBarChart points={data.throughput} />
                </div>

                <div>
                  <SectionTitle>Fluxo cumulativo</SectionTitle>
                  <CumulativeFlowChart days={data.cfd_days} points={data.cfd} />
                </div>

                <div>
                  <SectionTitle>Parados há mais tempo (em andamento ou aguardando)</SectionTitle>
                  <AgingList items={data.aging} />
                </div>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}
